import React from 'react';
import { Dropdown, Form, Spinner } from 'react-bootstrap';

// One page of async results returned by a MultiSelectFilter `loadPage`
// callback. nextCursor is opaque; the picker passes it back verbatim
// on the next call. undefined / empty string both mean "no more pages."
export interface MultiSelectFilterPage<T = any> {
  items: T[];
  nextCursor?: string;
}

// Popper modifier: when the dropdown opens inside a `.card` (the standard
// DataTable Card pattern), compute the distance from the toggle to the card
// edge in the placement direction and cap the menu's max-height to that
// space, with internal scroll. Without this, the dropdown's natural height
// can exceed the available card area and get visually clipped by the card's
// `overflow: hidden`. Re-runs on every Popper update (window resize, scroll),
// so the cap stays in sync as the viewport changes. No-op when there's no
// .card ancestor — dropdown falls back to its natural size.
const menuPopperConfig = {
  modifiers: [
    {
      name: 'sizeMenuToCard',
      enabled: true,
      phase: 'main' as const,
      fn: ({ state }: { state: any }) => {
        const popper = state.elements.popper as HTMLElement;
        const reference = state.elements.reference as HTMLElement;
        const card = popper.closest('.card') as HTMLElement | null;
        if (!card) return;
        const cardRect = card.getBoundingClientRect();
        const refRect = reference.getBoundingClientRect();
        const space = state.placement.startsWith('top')
          ? refRect.top - cardRect.top - 8
          : cardRect.bottom - refRect.bottom - 8;
        if (space > 0) {
          popper.style.maxHeight = `${space}px`;
          popper.style.overflowY = 'auto';
        }
      },
    },
  ],
};

// ========== TextFilter ==========

interface TextFilterProps {
  field: string;
  label: string;
  icon: string;
  placeholder: string;
  value?: string;
  onChange: (field: string, value: string | undefined) => void;
  isClearing?: boolean;
}

export const TextFilter: React.FC<TextFilterProps> = ({ field, label, icon, placeholder, value, onChange, isClearing }) => {
  const [tempValue, setTempValue] = React.useState(value || '');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    setTempValue(value || '');
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, [value]);

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleApply = () => {
    if (isClearing) return;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const newValue = tempValue || undefined;
    if (newValue !== value) {
      onChange(field, newValue);
    }
  };

  const handleInputChange = (newValue: string) => {
    setTempValue(newValue);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      onChange(field, newValue || undefined);
    }, 250);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
      const toggleButton = document.getElementById(`dropdown-${field}`);
      if (toggleButton) {
        toggleButton.click();
      }
    }
  };

  return (
    <Dropdown onToggle={(isOpen) => {
      if (!isOpen) {
        handleApply();
      } else {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }}>
      <Dropdown.Toggle
        variant={value ? "primary" : "outline-secondary"}
        size="sm"
        id={`dropdown-${field}`}
        className={value ? "d-flex align-items-center" : ""}
      >
        {value && (
          <>
            <i
              className="bi bi-x-circle me-2"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                onChange(field, undefined);
              }}
            ></i>
            <span className="border-end border-body-tertiary me-2" style={{ height: '1rem' }}></span>
          </>
        )}
        <i className={`bi ${icon} me-1`}></i>
        {label}{value && `: ${value}`}
      </Dropdown.Toggle>
      <Dropdown.Menu className="bg-header-control" data-bs-theme="light" popperConfig={menuPopperConfig}>
        <div className="px-3 py-2" style={{ minWidth: '250px' }} onClick={(e) => e.stopPropagation()}>
          <Form.Control
            ref={inputRef}
            size="sm"
            type="text"
            placeholder={placeholder}
            value={tempValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

// ========== SelectFilter ==========

interface SelectFilterProps {
  field: string;
  label: string;
  icon: string;
  value?: string;
  options: { value: string; label: string }[];
  onChange: (field: string, value: string | undefined) => void;
  required?: boolean; // hides the X-clear icon and the "All" menu item
}

export const SelectFilter: React.FC<SelectFilterProps> = ({ field, label, icon, value, options, onChange, required }) => (
  <Dropdown as="div" className="d-inline-block">
    <Dropdown.Toggle
      variant={value ? "primary" : "outline-secondary"}
      size="sm"
      id={`dropdown-${field}`}
      className={value ? "d-flex align-items-center" : ""}
    >
      {value && !required && (
        <>
          <i
            className="bi bi-x-circle me-2"
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              onChange(field, undefined);
            }}
          ></i>
          <span className="border-end border-body-tertiary me-2" style={{ height: '1rem' }}></span>
        </>
      )}
      <i className={`bi ${icon} me-1`}></i>
      {label}{value && `: ${options.find(o => o.value === value)?.label || value}`}
    </Dropdown.Toggle>
    <Dropdown.Menu className="bg-header-control" data-bs-theme="light" popperConfig={menuPopperConfig}>
      {!required && (
        <>
          <Dropdown.Item onClick={() => onChange(field, undefined)}>
            All
          </Dropdown.Item>
          <Dropdown.Divider />
        </>
      )}
      {options.map((option) => (
        <Dropdown.Item
          key={option.value}
          onClick={() => onChange(field, option.value)}
          active={value === option.value}
        >
          {option.label}
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);

// ========== MultiSelectFilter ==========

interface QuickPick {
  /** Display label for the row, e.g. "All programs". */
  label: string;
  /** Set of option values to apply on click. The full set replaces the
   *  current selection (no merge) so the user gets exactly what the
   *  label promises. */
  values: string[];
}

interface MultiSelectFilterProps {
  field: string;
  label: string;
  icon: string;
  value?: string; // comma-separated selected values
  onChange: (field: string, value: string | undefined) => void;
  /** Optional named shortcuts rendered above the option list, e.g.
   *  "All programs" → tick every program-eligible item in one click. */
  quickPicks?: QuickPick[];

  // ----- Static mode -----------------------------------------------------
  /** Static option list, filtered client-side by the search input.
   *  Mutually exclusive with `loadPage` — exactly one must be set. */
  options?: { value: string; label: string }[];

  // ----- Async paginated mode -------------------------------------------
  /** Cursor-paginated loader. When set, the dropdown fetches options on
   *  open + on every search change (debounced) + on scroll-to-bottom for
   *  the next page. The picker treats nextCursor as opaque. */
  loadPage?: (params: { search: string; cursor?: string; limit: number }) => Promise<MultiSelectFilterPage<any>>;
  /** Resolve labels for IDs already in the value but not yet paged to
   *  (e.g. on initial mount with URL-state). Optional — without it, IDs
   *  render as labels until the user finds them via search. */
  loadByIds?: (ids: string[]) => Promise<any[]>;
  /** Identity + display for async items. */
  getOptionValue?: (item: any) => string;
  getOptionLabel?: (item: any) => string;
  pageSize?: number;
  placeholder?: string;
  /** Static mode only. When true, the dropdown shows an Include / Exclude
   *  toggle. Exclude mode emits values with a `!` prefix in the CSV so the
   *  backend filters as "match rows that have NONE of these." See the
   *  matching field on the FilterDefinition multiselect variant for
   *  semantics. */
  allowExclude?: boolean;
}

/**
 * Multi-select dropdown filter. Single component, two modes:
 *
 *   - Static:  caller passes `options[]`. Client-side substring filter on
 *              the search input. Used for small enumerations (status,
 *              role, account_id, etc.).
 *   - Async:   caller passes `loadPage` (and optionally `loadByIds`).
 *              The picker hits the API on open, on every search change
 *              (debounced 300ms), and on scroll-to-bottom for the next
 *              page. Use this when the option set comes from a
 *              cursor-paginated endpoint — feeding only the first page
 *              into the static mode would silently truncate options
 *              past the first page.
 *
 * Tri-state value semantics in both modes:
 *   undefined  → DEFAULT — no filter applied (chip drops; toggle styled
 *                outline-secondary)
 *   ''         → EXPLICIT-NONE — filter to empty set
 *   'a,b,c'    → SUBSET — only the listed values
 *
 * Search input is always rendered. Tiny static enums get a search box
 * they don't strictly need; that's fine — typing to narrow a 3-row
 * dropdown isn't a usability bug.
 */
export const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  field, label, icon, value, onChange, quickPicks,
  options, loadPage, loadByIds, getOptionValue, getOptionLabel,
  pageSize = 50, placeholder = 'Search…',
  allowExclude = false,
}) => {
  const isAsync = !!loadPage;
  const [search, setSearch] = React.useState('');

  // ----- Async wire-format parse ---------------------------------------
  //
  // Async mode supports a richer wire format than positive-CSV so the
  // picker can express "all selected, except a few" — a state that's
  // common when the option set is paginated and the user wants to filter
  // OUT specific entries without enumerating every other entry. Format
  // (per api-spec.yaml customer_ids):
  //
  //     undefined          → ASYNC_UNSET / DEFAULT — no filter
  //     ''                 → ASYNC_NONE — match nothing
  //     'all'              → ASYNC_ALL — explicit "everything", same effect
  //                          as DEFAULT but lets the picker round-trip
  //                          through the chip without dropping
  //     'all,!a,!b'        → ASYNC_ALL_EXCEPT — match everyone except
  //                          {a, b}
  //     'a,b,c'            → ASYNC_SUBSET — match only {a, b, c}
  //
  // Static mode keeps the simpler tri-state from before (undefined / '' /
  // CSV) — no exclude-mode, since with a fully-known options[] the user
  // can express anything via positive subset.
  type AsyncMode = 'unset' | 'all' | 'all-except' | 'subset' | 'none';
  const asyncFilter = React.useMemo((): { mode: AsyncMode; ids: Set<string> } => {
    if (!isAsync) return { mode: 'unset', ids: new Set() };
    if (value === undefined) return { mode: 'unset', ids: new Set() };
    if (value === '') return { mode: 'none', ids: new Set() };
    const parts = value.split(',').map(p => p.trim()).filter(Boolean);
    if (parts.length === 0) return { mode: 'none', ids: new Set() };
    if (parts[0] === 'all') {
      const excludes = new Set<string>();
      for (let i = 1; i < parts.length; i++) {
        const p = parts[i];
        if (p.startsWith('!')) excludes.add(p.slice(1));
      }
      return excludes.size > 0
        ? { mode: 'all-except', ids: excludes }
        : { mode: 'all', ids: new Set() };
    }
    return { mode: 'subset', ids: new Set(parts) };
  }, [isAsync, value]);

  // ----- Static-mode value parse (positive CSV with optional `!` prefix) -
  const isDefaultStatic = !isAsync && value === undefined;
  const isExplicitNoneStatic = !isAsync && value === '';
  // Exclude mode is detected by ANY value starting with `!`. Mixed prefixes
  // would be rejected by the backend, so treat the whole filter as exclude
  // when any value carries the prefix. Selection set strips the prefix
  // either way — option.value never includes `!`.
  const staticParsed = React.useMemo(() => {
    if (isAsync || isDefaultStatic || isExplicitNoneStatic) {
      return { mode: 'include' as 'include' | 'exclude', set: new Set<string>() };
    }
    const set = new Set<string>();
    let anyExclude = false;
    for (const raw of value!.split(',')) {
      const v = raw.trim();
      if (!v) continue;
      if (v.startsWith('!')) {
        anyExclude = true;
        set.add(v.slice(1));
      } else {
        set.add(v);
      }
    }
    return { mode: (anyExclude ? 'exclude' : 'include') as 'include' | 'exclude', set };
  }, [isAsync, isDefaultStatic, isExplicitNoneStatic, value]);
  const staticMode = staticParsed.mode;
  const subsetSetStatic = staticParsed.set;

  // Unified isDefault / isExplicitNone — both modes know these states.
  const isDefault = isAsync ? (asyncFilter.mode === 'unset' || asyncFilter.mode === 'all') : isDefaultStatic;
  const isExplicitNone = isAsync ? asyncFilter.mode === 'none' : isExplicitNoneStatic;
  const filterActive = !isDefault;

  // ----- Static-mode derived state --------------------------------------
  const visibleStaticOptions = React.useMemo(() => {
    if (isAsync || !options) return [];
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, search, isAsync]);

  const allStaticValues = React.useMemo(() => options?.map(o => o.value) ?? [], [options]);
  // Track user's mode preference locally so toggling Include/Exclude
  // before any selection is reflected in the UI even though the URL
  // hasn't changed yet. Initialize from the parsed value; keep in sync
  // when value changes externally (URL nav, parent state, etc.).
  const [staticUserMode, setStaticUserMode] = React.useState<'include' | 'exclude'>(staticMode);
  React.useEffect(() => { setStaticUserMode(staticMode); }, [staticMode]);
  const effectiveStaticMode: 'include' | 'exclude' = allowExclude ? staticUserMode : 'include';

  // Checked-items set. In include mode, default (no filter) shows all
  // options as checked ("everything is shown"). In exclude mode, default
  // shows none as checked ("nothing is being excluded") — checking an
  // item adds it to the exclude list. Either way, subsetSetStatic carries
  // the explicitly-tracked items once the operator interacts.
  const staticSelected = isDefaultStatic
    ? (effectiveStaticMode === 'exclude' ? new Set<string>() : new Set(allStaticValues))
    : subsetSetStatic;

  // Toggle pill count.
  //   Static default → all (we show staticSelected.size = options.length)
  //   Static subset / none → subsetSetStatic.size
  //   Async all-except → "−N" where N is exclusions (string, signed)
  //   Async subset → N
  //   Async none → 0
  //   Async unset/all → no count shown anyway (filterActive=false)
  const countDisplay = React.useMemo((): string | null => {
    if (!filterActive) return null;
    if (isAsync) {
      if (asyncFilter.mode === 'all-except') return String(asyncFilter.ids.size);
      if (asyncFilter.mode === 'subset') return String(asyncFilter.ids.size);
      if (asyncFilter.mode === 'none') return '0';
      return null;
    }
    return String(staticSelected.size);
  }, [isAsync, asyncFilter, staticSelected, filterActive]);

  // ----- Async-mode state -----------------------------------------------
  const [open, setOpen] = React.useState(false);
  const [pages, setPages] = React.useState<any[]>([]);
  const [nextCursor, setNextCursor] = React.useState<string | undefined>(undefined);
  const [loading, setLoading] = React.useState(false);
  // Cache of resolved {id → item} for already-selected items, so the
  // "currently selected" rows keep their labels even when search
  // narrows them out of the current pages.
  const [selectedItems, setSelectedItems] = React.useState<Map<string, any>>(new Map());
  const genRef = React.useRef(0);
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadingRef = React.useRef(loading);
  loadingRef.current = loading;
  const nextCursorRef = React.useRef(nextCursor);
  nextCursorRef.current = nextCursor;
  const searchRef = React.useRef(search);
  searchRef.current = search;

  // Whatever IDs are tracked in the async value need their labels
  // resolved — for `subset`, the included rows render checked; for
  // `all-except`, the excluded rows render UNCHECKED at the top so
  // the user can see which ones they've removed. Either way the
  // dropdown wants their labels.
  const asyncTrackedIds = isAsync ? Array.from(asyncFilter.ids) : [];
  const asyncTrackedKey = asyncTrackedIds.join(',');

  // Resolve labels for tracked IDs (subset members or exclusions, both
  // need a display label).
  React.useEffect(() => {
    if (!isAsync || !loadByIds || !getOptionValue) return;
    const missing = asyncTrackedIds.filter(id => !selectedItems.has(id));
    if (missing.length === 0) return;
    let cancelled = false;
    loadByIds(missing).then(items => {
      if (cancelled) return;
      setSelectedItems(prev => {
        const next = new Map(prev);
        for (const item of items) next.set(getOptionValue(item), item);
        return next;
      });
    }).catch(() => { /* best effort */ });
    return () => { cancelled = true; };
    // asyncTrackedKey serializes the set so identity churn doesn't refire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncTrackedKey, isAsync, loadByIds, getOptionValue]);

  // Page loader.
  const fetchPage = React.useCallback(async (q: string, cursor: string | undefined, gen: number) => {
    if (!isAsync || !loadPage) return;
    setLoading(true);
    try {
      const result = await loadPage({ search: q, cursor, limit: pageSize });
      if (gen !== genRef.current) return;
      setPages(prev => cursor ? [...prev, ...result.items] : result.items);
      setNextCursor(result.nextCursor || undefined);
    } catch {
      // Best-effort.
    } finally {
      if (gen === genRef.current) setLoading(false);
    }
  }, [isAsync, loadPage, pageSize]);

  // First page on open + on every search change (debounced 300ms). We
  // do NOT clear `pages` here — keeping stale results visible during
  // the debounce window prevents the "No matches" branch from flashing
  // before the API response arrives. `loading=true` immediately
  // suppresses the empty-state branch.
  React.useEffect(() => {
    if (!isAsync || !open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const gen = ++genRef.current;
    setLoading(true);
    setNextCursor(undefined);
    debounceRef.current = setTimeout(() => {
      fetchPage(search, undefined, gen);
    }, search === '' ? 0 : 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [isAsync, open, search, fetchPage]);

  // Scroll-to-load-more sentinel. Observer kept alive across loading /
  // cursor changes (refs read inside the callback) so the freshly-
  // recreated observer doesn't fire its initial-state callback against
  // the still-not-yet-reflowed sentinel — same lesson as the DataTable
  // double-fetch fix.
  React.useEffect(() => {
    if (!isAsync || !open || !sentinelRef.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !loadingRef.current && nextCursorRef.current) {
        fetchPage(searchRef.current, nextCursorRef.current, genRef.current);
      }
    }, { threshold: 0.1, root: menuRef.current });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [isAsync, open, fetchPage]);

  // Tracked IDs (subset members for `subset` mode; exclusions for
  // `all-except`) that aren't in the current pages. Render them at the
  // top so the user always sees what's tracked, even when search
  // narrows them out of the page or they live on later pages.
  const orphanTrackedAsync = React.useMemo(() => {
    if (!isAsync || !getOptionValue) return [];
    const inPages = new Set(pages.map(getOptionValue));
    return asyncTrackedIds.filter(id => !inPages.has(id));
  }, [isAsync, pages, asyncTrackedKey, getOptionValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // serializeStatic emits a CSV for the given mode + selection, honoring
  // the "all checked = no filter" shortcut so the URL stays clean.
  // Returns undefined for the "no filter" case; '' for the deselect-all
  // / exclude-all sentinel.
  const serializeStatic = React.useCallback(
    (mode: 'include' | 'exclude', set: Set<string>, optionCount: number): string | undefined => {
      if (mode === 'include') {
        if (set.size === 0) return ''; // explicit-none
        if (set.size === optionCount) return undefined; // all selected = no filter
        return Array.from(set).join(',');
      }
      // exclude mode
      if (set.size === 0) return undefined; // exclude nothing = no filter
      if (set.size === optionCount) return ''; // exclude everything = match nothing
      return Array.from(set).map(v => '!' + v).join(',');
    },
    []
  );

  // ----- Toggle ---------------------------------------------------------
  const toggleStatic = (optionValue: string) => {
    if (!options) return;
    const next = new Set(staticSelected);
    if (next.has(optionValue)) next.delete(optionValue);
    else next.add(optionValue);
    onChange(field, serializeStatic(effectiveStaticMode, next, options.length));
  };

  // Mode toggle (Include / Exclude). Switching mode re-emits the current
  // selection with the new prefix. When the selection is empty, only the
  // local userMode state changes — the URL stays at "no filter".
  const setStaticMode = (newMode: 'include' | 'exclude') => {
    setStaticUserMode(newMode);
    if (!options) return;
    // If there's a non-trivial selection (subset, not all), re-emit it.
    // Default state (undefined) and explicit-none ('') both map to empty
    // selection sets in their respective modes; serializeStatic figures
    // out the right URL.
    if (subsetSetStatic.size === 0) {
      // Default / explicit-none: no-op for URL. The mode flag is local-only.
      // (An explicit-none value in include mode literally MEANS "select
      // nothing"; flipping to exclude mode should clear that to default.)
      if (isExplicitNoneStatic && newMode === 'exclude') {
        onChange(field, undefined);
      }
      return;
    }
    onChange(field, serializeStatic(newMode, subsetSetStatic, options.length));
  };

  // Encode an async filter back to its wire form. Inverse of asyncFilter
  // parsing above. See the `customer_ids` grammar in api-spec.yaml.
  const serializeAsync = (mode: AsyncMode, ids: Set<string>): string | undefined => {
    switch (mode) {
      case 'unset': return undefined;
      case 'all':   return ids.size > 0
                      ? `all,${Array.from(ids).map(id => `!${id}`).join(',')}`
                      : undefined; // all + 0 exclusions == default; collapse
      case 'all-except': return `all,${Array.from(ids).map(id => `!${id}`).join(',')}`;
      case 'subset':     return ids.size === 0 ? '' : Array.from(ids).join(',');
      case 'none':       return '';
    }
  };

  // Async toggle. The mode tells us how to interpret the click:
  //
  //   unset / all   + click X → switch to all-except, exclusions = {X}
  //   all-except    + click X already excluded → remove X from
  //                  exclusions (re-include); empty → revert to default
  //   all-except    + click X not excluded → add X to exclusions
  //   subset        + click X already included → remove X; empty → none
  //   subset        + click X not included → add X
  //   none          + click X → switch to subset = {X}
  //
  // Items NOT yet loaded keep their effective selection state because
  // it's encoded in the mode (all-except naturally includes them; subset
  // naturally excludes them). No more "deselect from default loses every
  // unloaded customer" footgun.
  const toggleAsync = (optionValue: string, item: any | undefined) => {
    if (item) {
      setSelectedItems(prev => {
        const m = new Map(prev);
        m.set(optionValue, item);
        return m;
      });
    }
    const { mode, ids } = asyncFilter;
    const next = new Set(ids);
    let nextMode: AsyncMode = mode;
    switch (mode) {
      case 'unset':
      case 'all':
        nextMode = 'all-except';
        next.add(optionValue);
        break;
      case 'all-except':
        if (next.has(optionValue)) {
          next.delete(optionValue);
          if (next.size === 0) nextMode = 'unset';
        } else {
          next.add(optionValue);
        }
        break;
      case 'subset':
        if (next.has(optionValue)) {
          next.delete(optionValue);
          if (next.size === 0) nextMode = 'none';
        } else {
          next.add(optionValue);
        }
        break;
      case 'none':
        nextMode = 'subset';
        next.add(optionValue);
        break;
    }
    onChange(field, serializeAsync(nextMode, next));
  };

  // ----- Quick picks ----------------------------------------------------
  const applyQuickPick = (values: string[]) => {
    if (values.length === 0) {
      onChange(field, '');
      return;
    }
    if (options && values.length === options.length) {
      onChange(field, undefined);
      return;
    }
    onChange(field, values.join(','));
  };

  // ----- Render ---------------------------------------------------------
  // Async mode controls `show` so we can hook into open/close to seed
  // the first page. Static mode lets Bootstrap manage open/close.
  const dropdownProps = isAsync
    ? { show: open, onToggle: (isOpen: boolean) => { setOpen(isOpen); if (!isOpen) setSearch(''); } }
    : {};

  return (
    <Dropdown as="div" className="d-inline-block" autoClose="outside" {...dropdownProps}>
      <Dropdown.Toggle
        variant={filterActive ? "primary" : "outline-secondary"}
        size="sm"
        id={`dropdown-${field}`}
        className={filterActive ? "d-flex align-items-center" : ""}
      >
        {filterActive && (
          <>
            <i
              className="bi bi-x-circle me-2"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                onChange(field, undefined);
              }}
            ></i>
            <span className="border-end border-body-tertiary me-2" style={{ height: '1rem' }}></span>
          </>
        )}
        <i className={`bi ${icon} me-1`}></i>
        {label}{countDisplay !== null && ` (${countDisplay})`}
      </Dropdown.Toggle>
      <Dropdown.Menu className="bg-header-control" data-bs-theme="light" popperConfig={menuPopperConfig}>
        {/* Include / Exclude mode toggle (static mode only, opt-in via
            allowExclude). Shows current mode and lets operator flip;
            checked items mean "include" or "exclude" respectively. */}
        {!isAsync && allowExclude && (
          <>
            <div className="px-3 py-2 d-flex align-items-center gap-2 small">
              <span className="text-muted">Mode:</span>
              <div className="btn-group btn-group-sm" role="group" aria-label="Filter mode">
                <button
                  type="button"
                  className={`btn ${effectiveStaticMode === 'include' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setStaticMode('include')}
                  title="Include: show rows that have ANY of the checked categories."
                >
                  Include
                </button>
                <button
                  type="button"
                  className={`btn ${effectiveStaticMode === 'exclude' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setStaticMode('exclude')}
                  title="Exclude: show rows that have NONE of the checked categories."
                >
                  Exclude
                </button>
              </div>
            </div>
            <Dropdown.Divider />
          </>
        )}
        <Dropdown.Item
          onClick={() => onChange(field, serializeStatic(effectiveStaticMode, new Set(allStaticValues), allStaticValues.length))}
          disabled={isDefault || (!isAsync && (options?.length ?? 0) === 0)}
          title={effectiveStaticMode === 'exclude'
            ? "Check every category (in exclude mode this means exclude everything = match no rows)."
            : "Check every category = no filter applied."}
        >
          <i className="bi bi-check-all me-2"></i>
          {effectiveStaticMode === 'exclude' ? 'Check all (exclude all)' : 'Select all'}
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() => onChange(field, serializeStatic(effectiveStaticMode, new Set(), allStaticValues.length))}
          disabled={isExplicitNone || (!isAsync && (options?.length ?? 0) === 0)}
          title={effectiveStaticMode === 'exclude'
            ? "Uncheck everything (in exclude mode this means exclude nothing = no filter)."
            : "Uncheck everything (in include mode this means match no rows)."}
        >
          <i className="bi bi-x-circle me-2"></i>
          {effectiveStaticMode === 'exclude' ? 'Uncheck all (no exclusions)' : 'Deselect all'}
        </Dropdown.Item>
        {quickPicks && quickPicks.length > 0 && (
          <>
            <Dropdown.Divider />
            {quickPicks.map((qp) => (
              <Dropdown.Item key={`qp:${qp.label}`} onClick={() => applyQuickPick(qp.values)}>
                <i className="bi bi-lightning-charge me-2"></i>
                {qp.label}
                <span className="text-muted small ms-2">({qp.values.length})</span>
              </Dropdown.Item>
            ))}
          </>
        )}
        <Dropdown.Divider />
        <div className="px-2 pb-2">
          <div className="position-relative">
            <Form.Control
              size="sm"
              type="text"
              placeholder={placeholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              style={{ paddingRight: search ? '2rem' : undefined }}
            />
            {search && (
              <i
                className="bi bi-x-circle position-absolute top-50 end-0 translate-middle-y text-muted me-2"
                style={{ cursor: 'pointer' }}
                onClick={(e) => { e.stopPropagation(); setSearch(''); }}
                role="button"
                aria-label="Clear search"
              ></i>
            )}
          </div>
        </div>

        {!isAsync && visibleStaticOptions.map((option) => (
          <Dropdown.Item
            key={option.value}
            onClick={() => toggleStatic(option.value)}
            active={staticSelected.has(option.value)}
          >
            <Form.Check
              type="checkbox"
              label={option.label}
              checked={staticSelected.has(option.value)}
              onChange={() => {}}
              className="mb-0"
            />
          </Dropdown.Item>
        ))}

        {!isAsync && search && visibleStaticOptions.length === 0 && (options?.length ?? 0) > 0 && (
          <Dropdown.Header className="text-muted small">No matches</Dropdown.Header>
        )}

        {isAsync && (
          <div ref={menuRef} style={{ maxHeight: 320, overflowY: 'auto' }}>
            {/*
              Render rule by mode:
                unset / all  → every loaded row checked (no orphans —
                               there's nothing tracked)
                all-except   → every loaded row checked except the
                               exclusions; orphans (excluded ids not in
                               current pages) shown UNCHECKED at top
                subset       → loaded rows checked iff in subset; orphans
                               (subset members not in current pages)
                               shown CHECKED at top
                none         → nothing checked
            */}
            {orphanTrackedAsync.map((id) => {
              const item = selectedItems.get(id);
              const checked = asyncFilter.mode === 'subset';
              return (
                <Dropdown.Item
                  key={`tracked:${id}`}
                  onClick={() => toggleAsync(id, item)}
                  active={checked}
                >
                  <Form.Check
                    type="checkbox"
                    label={item && getOptionLabel ? getOptionLabel(item) : id}
                    checked={checked}
                    onChange={() => {}}
                    className="mb-0"
                  />
                </Dropdown.Item>
              );
            })}
            {pages.map((item) => {
              const v = getOptionValue!(item);
              let isSelected: boolean;
              switch (asyncFilter.mode) {
                case 'unset':
                case 'all':
                  isSelected = true; break;
                case 'all-except':
                  isSelected = !asyncFilter.ids.has(v); break;
                case 'subset':
                  isSelected = asyncFilter.ids.has(v); break;
                case 'none':
                  isSelected = false; break;
              }
              return (
                <Dropdown.Item
                  key={v}
                  onClick={() => toggleAsync(v, item)}
                  active={isSelected}
                >
                  <Form.Check
                    type="checkbox"
                    label={getOptionLabel!(item)}
                    checked={isSelected}
                    onChange={() => {}}
                    className="mb-0"
                  />
                </Dropdown.Item>
              );
            })}
            {loading && (
              <div className="text-center py-2">
                <Spinner size="sm" animation="border" />
              </div>
            )}
            {!loading && pages.length === 0 && orphanTrackedAsync.length === 0 && (
              <Dropdown.Header className="text-muted small">
                {search ? 'No matches' : 'Type to search'}
              </Dropdown.Header>
            )}
            <div ref={sentinelRef} style={{ height: 1 }} />
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

// ========== AmountFilter ==========
//
// Structured amount widget. Manipulates three filter keys simultaneously:
//   - directionField     (string): 'incoming' | 'outgoing' | undefined (both)
//   - amountMinField     (string): minimum |amount|
//   - amountMaxField     (string): maximum |amount|
//
// One numeric field set → exact match on |amount|; both set → BETWEEN.
//
// "Both directions" is the same as not filtering by direction at all, so
// when the user has both checkboxes checked we leave directionField empty.

interface AmountFilterProps {
  label: string;
  icon: string;
  directionField: string;
  amountMinField: string;
  amountMaxField: string;
  direction?: string;
  amountMin?: string;
  amountMax?: string;
  onChange: (field: string, value: string | undefined) => void;
}

export const AmountFilter: React.FC<AmountFilterProps> = ({
  label,
  icon,
  directionField,
  amountMinField,
  amountMaxField,
  direction,
  amountMin,
  amountMax,
  onChange,
}) => {
  // Local working state for the numeric fields so typing doesn't fire a
  // request per keystroke. Committed on blur / Apply.
  const [minStr, setMinStr] = React.useState(amountMin || '');
  const [maxStr, setMaxStr] = React.useState(amountMax || '');
  React.useEffect(() => { setMinStr(amountMin || ''); }, [amountMin]);
  React.useEffect(() => { setMaxStr(amountMax || ''); }, [amountMax]);

  // Radio-style direction picker: exactly one of all/incoming/outgoing
  // is selected at any time. "all" is encoded as undefined on the wire.
  const directionValue = direction === 'incoming' || direction === 'outgoing' ? direction : 'all';
  const setDirection = (v: 'all' | 'incoming' | 'outgoing') => {
    onChange(directionField, v === 'all' ? undefined : v);
  };

  const commitMin = () => {
    const v = minStr.trim() || undefined;
    if (v !== amountMin) onChange(amountMinField, v);
  };
  const commitMax = () => {
    const v = maxStr.trim() || undefined;
    if (v !== amountMax) onChange(amountMaxField, v);
  };

  // Pill summary: shows direction shorthand + amount range when set.
  const hasFilter = !!direction || !!amountMin || !!amountMax;
  const summary = (() => {
    const parts: string[] = [];
    if (direction === 'incoming') parts.push('in');
    else if (direction === 'outgoing') parts.push('out');
    if (amountMin && amountMax) parts.push(`$${amountMin}–$${amountMax}`);
    else if (amountMin) parts.push(`$${amountMin}`);
    else if (amountMax) parts.push(`$${amountMax}`);
    return parts.join(' · ');
  })();

  return (
    <Dropdown as="div" className="d-inline-block" autoClose="outside">
      <Dropdown.Toggle
        variant={hasFilter ? 'primary' : 'outline-secondary'}
        size="sm"
        id="dropdown-amount"
        className={hasFilter ? 'd-flex align-items-center' : ''}
      >
        {hasFilter && (
          <>
            <i
              className="bi bi-x-circle me-2"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                onChange(directionField, undefined);
                onChange(amountMinField, undefined);
                onChange(amountMaxField, undefined);
              }}
            ></i>
            <span className="border-end border-body-tertiary me-2" style={{ height: '1rem' }}></span>
          </>
        )}
        <i className={`bi ${icon} me-1`}></i>
        {label}{hasFilter && `: ${summary}`}
      </Dropdown.Toggle>
      <Dropdown.Menu className="bg-header-control" data-bs-theme="light" popperConfig={menuPopperConfig}>
        <div className="px-3 py-2" style={{ minWidth: '260px' }} onClick={(e) => e.stopPropagation()}>
          <div className="d-flex gap-3 mb-2">
            <Form.Check
              type="radio"
              name="amount-direction"
              id="amount-direction-all"
              label="All"
              checked={directionValue === 'all'}
              onChange={() => setDirection('all')}
            />
            <Form.Check
              type="radio"
              name="amount-direction"
              id="amount-direction-incoming"
              label="Incoming"
              checked={directionValue === 'incoming'}
              onChange={() => setDirection('incoming')}
            />
            <Form.Check
              type="radio"
              name="amount-direction"
              id="amount-direction-outgoing"
              label="Outgoing"
              checked={directionValue === 'outgoing'}
              onChange={() => setDirection('outgoing')}
            />
          </div>
          <div className="d-flex gap-2 align-items-center">
            <Form.Control
              size="sm"
              type="number"
              step="0.01"
              placeholder="Min"
              value={minStr}
              onChange={(e) => setMinStr(e.target.value)}
              onBlur={commitMin}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitMin(); } }}
            />
            <span className="text-muted">–</span>
            <Form.Control
              size="sm"
              type="number"
              step="0.01"
              placeholder="Max"
              value={maxStr}
              onChange={(e) => setMaxStr(e.target.value)}
              onBlur={commitMax}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitMax(); } }}
            />
          </div>
          <div className="small text-muted mt-1">
            Enter one value for an exact match, two for a range.
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

// ========== FilterBar ==========

export type FilterDefinition = {
  type: 'text';
  field: string;
  label: string;
  icon: string;
  placeholder: string;
} | {
  type: 'select';
  field: string;
  label: string;
  icon: string;
  options: { value: string; label: string }[];
  /** When true, the select cannot be cleared — no X icon, no "All" item.
   *  Use for filters that the page treats as required (e.g. "Income by"
   *  on the Financial Reports page, where the API rejects requests
   *  without it). */
  required?: boolean;
} | {
  /** Multi-select chip + dropdown. Provide either `options[]` (static,
   *  client-side filtered) OR `loadPage` (async paginated, server-side
   *  filtered). The component picks the mode based on which is set. */
  type: 'multiselect';
  field: string;
  label: string;
  icon: string;
  /** Optional named shortcuts rendered above the option list. */
  quickPicks?: { label: string; values: string[] }[];

  // Static mode
  options?: { value: string; label: string }[];

  // Async paginated mode
  loadPage?: (params: { search: string; cursor?: string; limit: number }) => Promise<MultiSelectFilterPage<any>>;
  loadByIds?: (ids: string[]) => Promise<any[]>;
  getOptionValue?: (item: any) => string;
  getOptionLabel?: (item: any) => string;
  pageSize?: number;
  placeholder?: string;
  /** When true, the dropdown shows an Include / Exclude toggle. In Exclude
   *  mode each checked option is emitted with a `!` prefix in the CSV
   *  (`!a,!b`) and the backend filter treats it as "match rows that have
   *  NONE of these." Useful for multi-tag rows (e.g. commission diff
   *  categories) where the operator's intent is "hide noise from these
   *  specific tags" rather than "show only these tags." Static mode
   *  only — async filters use the customer_ids `all,!id` grammar. */
  allowExclude?: boolean;
} | {
  type: 'amount';
  field: string;
  label: string;
  icon: string;
  directionField: string;
  amountMinField: string;
  amountMaxField: string;
};

interface FilterBarProps {
  filters: Record<string, any>;
  filterDefs: FilterDefinition[];
  excludeFromCount?: string[]; // keys to exclude from active filter count (e.g. 'dateRange')
  onFilterChange: (field: string, value: string | undefined) => void;
  onClearFilters: () => void;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  filterDefs,
  excludeFromCount = [],
  onFilterChange,
  onClearFilters,
}) => {
  const [isClearing, setIsClearing] = React.useState(false);

  const excludeSet = new Set(excludeFromCount);
  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    return value && !excludeSet.has(key);
  }).length;

  const hasActiveFilters = activeFilterCount > 0;

  return (
    <div className="ms-1 bg-header-control border-bottom p-1 me-1 rounded">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        {filterDefs.map((def) => {
          if (def.type === 'text') {
            return (
              <TextFilter
                key={def.field}
                field={def.field}
                label={def.label}
                icon={def.icon}
                placeholder={def.placeholder}
                value={filters[def.field]}
                onChange={onFilterChange}
                isClearing={isClearing}
              />
            );
          } else if (def.type === 'multiselect') {
            return (
              <MultiSelectFilter
                key={def.field}
                field={def.field}
                label={def.label}
                icon={def.icon}
                value={filters[def.field]}
                onChange={onFilterChange}
                quickPicks={def.quickPicks}
                options={def.options}
                loadPage={def.loadPage}
                loadByIds={def.loadByIds}
                getOptionValue={def.getOptionValue}
                getOptionLabel={def.getOptionLabel}
                pageSize={def.pageSize}
                placeholder={def.placeholder}
                allowExclude={def.allowExclude}
              />
            );
          } else if (def.type === 'amount') {
            return (
              <AmountFilter
                key={def.field}
                label={def.label}
                icon={def.icon}
                directionField={def.directionField}
                amountMinField={def.amountMinField}
                amountMaxField={def.amountMaxField}
                direction={filters[def.directionField]}
                amountMin={filters[def.amountMinField]}
                amountMax={filters[def.amountMaxField]}
                onChange={onFilterChange}
              />
            );
          } else {
            return (
              <SelectFilter
                key={def.field}
                field={def.field}
                label={def.label}
                icon={def.icon}
                value={filters[def.field]}
                options={def.options}
                onChange={onFilterChange}
                required={def.required}
              />
            );
          }
        })}

        {hasActiveFilters && (
          <button
            className="btn btn-outline-danger btn-sm ms-auto"
            onClick={() => {
              setIsClearing(true);
              onClearFilters();
              setTimeout(() => setIsClearing(false), 0);
            }}
          >
            <i className="bi bi-x-circle me-1"></i>
            Clear All
          </button>
        )}
      </div>
    </div>
  );
};
